import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { EventConfigData } from 'src/app/@data-services/event-config/event-config.data-model';
import { EventConfigDataService } from 'src/app/@data-services/event-config/event-config.data-service';
import { eventIdentifierMapper } from 'src/app/@helpers/event-identifier-mapper';
import { EventConfigStateService } from 'src/app/@stores/event-config-state.service';
import { applyDocumentTitle } from 'src/app/@utils/apply-document-title';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EventConfigService {
  private readonly _eventConfigDataService = inject(EventConfigDataService);
  private readonly _eventConfigStateService = inject(EventConfigStateService);

  loadEventConfig(
    eventName: string,
    registrationId: string = ''
  ): Observable<EventConfigData | null> {
    const slug = String(eventName ?? '').trim();
    const slugLower = slug.toLowerCase();
    const eventNameIdentifier = eventIdentifierMapper(slug) || slug;
    const prodDomain = `${slugLower}.synopsis.rozie.ai`;
    const domain = environment.production
      ? prodDomain
      : `dev-${slugLower}.synopsis.rozie.ai`;

    this._eventConfigStateService.setLoading(true);
    this._eventConfigStateService.setError(null);

    const applyConfig = (data: EventConfigData) => {
      this._eventConfigStateService.setEventConfig(data);
      applyDocumentTitle(data, data.Features?.ShowRozieFavicon ?? true);
      this._eventConfigStateService.setLoading(false);
      return data;
    };

    const loadByEventId = () =>
      this._eventConfigDataService
        .getEventConfig({
          eventNameIdentifier,
          registrationId,
        })
        .pipe(
          map((fallback) => {
            if (fallback.success && fallback.data) {
              return applyConfig(fallback.data);
            }
            this._eventConfigStateService.setError(
              'Failed to load event configuration'
            );
            this._eventConfigStateService.setLoading(false);
            return null;
          }),
          catchError(() => {
            this._eventConfigStateService.setError(
              'Error loading event configuration'
            );
            this._eventConfigStateService.setLoading(false);
            return of(null);
          })
        );

    // Non-prod: Domain in Dynamo is often saved without `dev-` (same as prod
    // host). Try that next so KnowledgeHubConfig.LoginType is not missed.
    // Query by domain only (matches how admins/curl usually look it up).
    const loadWithoutDevPrefix = () => {
      if (environment.production || domain === prodDomain) {
        return loadByEventId();
      }
      return this._eventConfigDataService
        .getEventConfig({
          domain: prodDomain,
          registrationId,
        })
        .pipe(
          switchMap((response) => {
            if (response.success && response.data) {
              return of(applyConfig(response.data));
            }
            return loadByEventId();
          }),
          catchError(() => loadByEventId())
        );
    };

    return this._eventConfigDataService
      .getEventConfig({
        domain,
        eventNameIdentifier,
        registrationId,
      })
      .pipe(
        switchMap((response) => {
          if (response.success && response.data) {
            return of(applyConfig(response.data));
          }
          return loadWithoutDevPrefix();
        }),
        catchError((error) => {
          console.error('Error loading event config:', error);
          return loadWithoutDevPrefix();
        })
      );
  }
}
